import type { IncidentGroup, IncidentKpis, IncidentOpsQueuesResponse } from '../../services/incidentsApi';
import { isIncidentGroupRecurringHotspot } from '../../domain/incidents/incidentPatternSemantics';

export type AnalyticsTimeRange = '1h' | '6h' | '24h' | '7d';

export type IncidentFlowPoint = {
  time: string;
  created: number;
  closed: number;
};

export type BreakdownPoint = {
  name: string;
  value: number;
};

export type IncidentOverviewMetrics = {
  openIncidents: number;
  openPatternCases: number;
  openObservations: number;
  openZones: number;
  highRiskUnresolved: number;
  /** In-window HIGH+CRITICAL observation volume (sum of open-group alert_count). */
  highSeverityObservations: number;
  overdueCount: number;
  recurringItems: number;
  recurringLocations: number;
  closedToday: number;
  newToday: number;
  myQueueCount: number;
  escalationsCount: number;
  unassignedHighRiskCount: number;
  exposureTrend: number;
};

const RESOLVED_GROUP_STATUSES = new Set(['closed', 'suppressed']);

export function deriveHoursFromAnalyticsRange(range: AnalyticsTimeRange): number {
  if (range === '1h') return 1;
  if (range === '6h') return 6;
  if (range === '7d') return 24 * 7;
  return 24;
}

export function deriveDateRangeFromAnalyticsRange(range: AnalyticsTimeRange): { fromDate: string; toDate: string } {
  const toDate = new Date();
  const fromDate = new Date(toDate);
  if (range === '1h') fromDate.setHours(toDate.getHours() - 1);
  else if (range === '6h') fromDate.setHours(toDate.getHours() - 6);
  else if (range === '7d') fromDate.setDate(toDate.getDate() - 7);
  else fromDate.setDate(toDate.getDate() - 1);
  return { fromDate: fromDate.toISOString(), toDate: toDate.toISOString() };
}

export function formatCategoryName(value: string): string {
  return String(value || 'unknown')
    .trim()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function buildIncidentOverviewMetrics(args: {
  kpis: IncidentKpis | null;
  opsQueues: IncidentOpsQueuesResponse | null;
  groups: IncidentGroup[];
  fallbackToKpisWhenNoGroups?: boolean;
}): IncidentOverviewMetrics {
  const groupedItems = Array.isArray(args.groups) ? args.groups : [];
  const openGroups = groupedItems.filter((group) => {
    const status = String(group?.status || '').trim().toLowerCase();
    return !RESOLVED_GROUP_STATUSES.has(status);
  });
  const recurringGroups = groupedItems.filter((group) => {
    return isIncidentGroupRecurringHotspot(group);
  });

  const openPatternCases = openGroups.length;
  const openObservations = Number(args.kpis?.open_now || 0);
  const highSeverityOpenGroups = openGroups.filter((group) => {
    const severity = String(group?.severity || '').toLowerCase();
    return severity === 'high' || severity === 'critical';
  });

  return {
    openIncidents:
      openPatternCases > 0
        ? openPatternCases
        : groupedItems.length === 0 && args.fallbackToKpisWhenNoGroups !== false
          ? openObservations
          : 0,
    openPatternCases,
    openObservations,
    openZones: new Set(openGroups.map((group) => String(group.zone_id || '').trim()).filter(Boolean)).size,
    highRiskUnresolved: highSeverityOpenGroups.length,
    highSeverityObservations: highSeverityOpenGroups.reduce(
      (sum, group) => sum + Math.max(0, Number(group?.alert_count || 0)),
      0,
    ),
    overdueCount: Number(args.opsQueues?.queues?.overdue?.count || 0),
    recurringItems: recurringGroups.length,
    recurringLocations: new Set(
      recurringGroups
        .map((group) => String(group.zone_id || group.camera_id || '').trim())
        .filter(Boolean),
    ).size,
    closedToday: Number(args.kpis?.resolved_today || 0),
    newToday: Number(args.kpis?.new_today || 0),
    myQueueCount: Number(args.opsQueues?.queues?.my_queue?.count || 0),
    escalationsCount: Number(args.opsQueues?.queues?.escalations?.count || 0),
    unassignedHighRiskCount: Number(args.opsQueues?.queues?.unassigned_high_risk?.count || 0),
    exposureTrend: Number(args.kpis?.new_today || 0) - Number(args.kpis?.resolved_today || 0),
  };
}

export function buildPercentBreakdown(items: BreakdownPoint[]): Array<BreakdownPoint & { label: string; percent: number }> {
  const total = items.reduce((sum, item) => sum + Number(item.value || 0), 0);
  return [...items]
    .sort((a, b) => Number(b.value || 0) - Number(a.value || 0))
    .map((item) => ({
      ...item,
      label: formatCategoryName(item.name || 'unknown'),
      percent: total > 0 ? Math.round((Number(item.value || 0) / total) * 100) : 0,
    }));
}

export function buildGroupBreakdown(
  groups: IncidentGroup[],
  field: 'incident_type' | 'severity',
): BreakdownPoint[] {
  const bucket = new Map<string, number>();
  (groups || []).forEach((group) => {
    const raw = String(group?.[field] || 'unknown').trim().toLowerCase() || 'unknown';
    const weight = Math.max(1, Number(group?.alert_count || group?.evidence_count || 1));
    bucket.set(raw, Number(bucket.get(raw) || 0) + weight);
  });
  return Array.from(bucket.entries()).map(([name, value]) => ({ name, value }));
}

export function buildFlowHighlights(flow: IncidentFlowPoint[]): {
  peakIntake: { time: string; created: number } | null;
  largestBacklogShift: { time: string; delta: number } | null;
} {
  const peakIntake = flow.reduce((peak, point) => {
    const created = Number(point.created || 0);
    return !peak || created > peak.created ? { time: point.time, created } : peak;
  }, null as { time: string; created: number } | null);

  const largestBacklogShift = flow.reduce((peak, point) => {
    const delta = Number(point.created || 0) - Number(point.closed || 0);
    return !peak || delta > peak.delta ? { time: point.time, delta } : peak;
  }, null as { time: string; delta: number } | null);

  return { peakIntake, largestBacklogShift };
}
